import { html, LitElement, css, unsafeCSS, render } from 'lit';

import '../components/comments';
import '../components/discorduser';
import '../parts/card';

import style from './build.scss';
import { parseHTML } from '../shared/parse';
import { convertName, loadURLParams, setTitle, trackPage } from '../shared/utils';
import { DATA_CLASSES, DATA_VERSIONS } from '../data/sources';
import { DIFFICULTY } from '../shared/types';

export class BuildPage extends LitElement {

    static get properties() {
        return {      
            build: Object,
            artwork: String,
            error: String,
            cards: Array      
        }
    }

    constructor() {
        super();  
        this.updating = null;
        this.build = null;
        this.path = '';
        this.error = null;
        this.name = null;
        this.artwork = null;
        this.diff = null;
        this.class = null;
        this.ver = null;
        this.parts = {
            Intro: '',
            Cards: '',
            Imbues: '',
            Talents: '',
            Strategy: ''
        };
        this.cards = [];
    }

    static styles = css`
        ${unsafeCSS(style)}        
    `;

    connectedCallback() {

        super.connectedCallback();      
        trackPage();

        let params = {
            id: ''
        };
        
        loadURLParams(params);

        this.updating = params.id;

        this.path = location.pathname.split('/')[2];

        if(this.path === 'create'){
            
            setTitle('Create Build');

            this.fetchCards();
            return;
        }      
        
        this.fetchData(this.updating||this.path);
    }

    async fetchCards(){
        let cardres = await fetch(`./api/cardsdropdown`);
        this.cards = await cardres.json();
    }

    async fetchData(uid) {        

        if(this.updating)
            await this.fetchCards();

        let res = await fetch(`./api/build/${uid}`);
        if(res.status === 200){            
            this.build = await res.json();    
        }else{
            this.error = 'Error: Build not found.';
        }

        if(this.build){
            
            setTitle((this.updating?'Edit ':'')+'Build | '+this.build.name+' | '+convertName(this.build.class));                
            
            if(this.updating){
                this.name = this.build.name;
                this.artwork = this.build.artwork;
                this.class = this.build.class;
                this.diff = this.build.diff;
                this.ver = this.build.version;                
                this.parts.Intro = this.build.parts[0];
                this.parts.Cards = this.build.parts[1];
                this.parts.Imbues = this.build.parts[2];
                this.parts.Talents = this.build.parts[3];
                this.parts.Strategy = this.build.parts[4];
            }
        }
    }

    async createBuild(){  
        
        this.error = null;

        const build = {
            name: this.name,
            artwork: this.artwork,
            class: this.class,
            diff: this.diff,
            version: this.ver,            
            parts: [this.parts.Intro,this.parts.Cards,this.parts.Imbues,this.parts.Talents,this.parts.Strategy]
        }

        Object.keys(build).forEach(k=>{
            if(build[k] === null){
                this.error = `Please input a value for ${k}`;
                return;
            }
        })

        if(!this.error){
            
            let res = await fetch('./api/createbuild',{
                method: 'POST',
                headers: {
                    'Accept': '*/*',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    build: build
                })
            });
    
            let json = await res.json();
            if(json.id){
                window.location = `./build/${json.id}`;
            }else{
                this.error = 'Error: Build creation failed.';
            }
        }        
    }

    async updateBuild(){  
        
        this.error = null;

        const build = {
            name: this.name,
            artwork: this.artwork,
            class: this.class,
            diff: this.diff,
            version: this.ver,   
            updating: this.updating,         
            parts: [this.parts.Intro,this.parts.Cards,this.parts.Imbues,this.parts.Talents,this.parts.Strategy]
        }

        Object.keys(build).forEach(k=>{
            if(build[k] === null){
                this.error = `Please input a value for ${k}`;
                return;
            }
        })

        if(!this.error){
            
            let res = await fetch('./api/createbuild',{
                method: 'POST',
                headers: {
                    'Accept': '*/*',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    build: build
                })
            });
    
            let json = await res.json();
            if(json.id){
                window.location = `./build/${json.id}`;
            }else{
                this.error = 'Error: Build update failed.';
            }
        }        
    }

    addCommentPart(type,id){
        let comment = this.shadowRoot.querySelector(`#${id}`);
        let val = prompt(`Please enter the ${type} name:`);
        if(val){
            comment.value += `[${type}=${val}]`;
            let prev = this.shadowRoot.querySelector(`#${id}preview`);
            let template = parseHTML(comment.value,true);
            render(template,prev);
            comment.focus();
        }
    }

    render() {            
        if(this.path === 'create' || this.path === 'update'){
            const parts = Object.keys(this.parts);
            const help = ['Add a brief description of the build.','Add a list of cards for the build.','List of imbues for the build.','List of talents for the build.','What is the core strategy of the build? What is the card rotation?'];
            const example = [
                'The Poison Rogue Build focuses on using poisonous attacks to inflict damage and debuffs on enemies.',
                `[card=Khanjali] (unlock at level XVI)
[card=Toxic Salve] to apply [status=Poison]
[card=Plague Strike] for burst damage
[card=Cloudkill] for burst damage (optional)
[card=Contaminate] (optional, unlock at level XIV)`,
                `[card=Poison Coating]
[card=Venomancing]
[card=Avatar of Blight] for energy management`,
                `[card=Baneful Weapon] (weapon power, unlock at level XI)
[card=Blighter]`,
                `Build up stacks of [status=Poison] on the target using basic attacks ([card=Khanjali] or [card=Poison Coating]) and cards like [card=Toxic Salve].

Trigger burst damage with [card=Plague Strike] or [card=Cloudkill] alternatively use [card=Venomancing] with basic attacks to inflict significant damage.

[status=Poison] damage can also be boosted with [card=Contaminate] or [card=Blighter]. For almost unlimited energy use [card=Avatar of Blight].`];
            return html`                
                <h1><input style="font-size: 30px" type="text" value=${this.name||'New Build'} @change=${(ev=>this.name=ev.target.value)} /></h1> 
                ${this.artwork?html`<card-part id=${this.artwork} imageonly="true"></card-part>`:''}
                <select @change=${(ev)=>{
                        this.artwork = ev.target.value;                        
                    }}>
                    <option value="">-- Artwork --</option>
                    ${this.cards.map((val,i)=>{
                        if(val)
                            return html`<option value="${val.id}" ?selected=${+this.artwork === val.id}>${val.name}</option>`;
                    })}
                </select>  
                <select @change=${(ev)=>{
                        this.diff = ev.target.value;
                    }}>
                    <option value="">-- Difficulty --</option>
                    ${DIFFICULTY.map((val,i)=>{
                        if(val)
                            return html`<option value="${val}" ?selected=${this.diff === val}>${val}</option>`;
                    })}
                </select>  
                <select @change=${(ev)=>{
                        this.class = ev.target.value;                        
                    }}>
                    <option value="">-- Class --</option>
                    ${[...DATA_CLASSES,{name:'Sunforge',value:'Scion'}].map((val,i)=>{
                        if(val)
                            return html`<option value="${val.value || val.name}" ?selected=${this.class === (val.value || val.name)}>${val.name}</option>`;
                    })}
                </select>   
                <select @change=${(ev)=>{
                        this.ver = ev.target.value;
                    }}>
                    <option value="">-- Version --</option>
                    ${DATA_VERSIONS.map((val,i)=>{
                        if(val)
                            return html`<option value="${val}" ?selected=${this.ver === val}>${val}</option>`;
                    })}
                </select>   
                ${parts.map((part,i)=>html`
                    <h3>${part}</h3>
                    <small>${help[i]}</small><br>
                    <div class="button small" @click=${()=>this.addCommentPart('card',part)}>Add Card</div>        
                    <div class="button small" @click=${()=>this.addCommentPart('talent',part)}>Add Talent</div>        
                    <div class="button small" @click=${()=>this.addCommentPart('monster',part)}>Add Monster</div>        
                    <div class="button small" @click=${()=>this.addCommentPart('event',part)}>Add Event</div>      
                    <div class="button small" @click=${()=>this.addCommentPart('status',part)}>Add Status</div>
                    <div class="button small" @click=${()=>this.addCommentPart('affix',part)}>Add Affix</div>  
                    <div class="button small" style="float:right;" @click=${()=>{
                        this.shadowRoot.querySelector('#'+part).value='';
                        this.shadowRoot.querySelector('#'+part+'preview').innerHTML='';
                    }}>Clear</div>
                    <textarea id=${part} placeholder="" rows="10" @keyup=${ev=>{
                        let prev = this.shadowRoot.querySelector('#'+part+'preview');                        
                        let template = parseHTML(ev.target.value,true);
                        render(template,prev);
                    }} @change=${ev=>this.parts[part] = ev.target.value}>${this.parts[part]||`Example:\n${example[i]}`}</textarea>
                    <div id=${part+'preview'} class="preview">     
                    </div>                    
                `)}                               
                ${this.error?html`<p style="color:red">${this.error}</p>`:''}
                ${this.updating ? html`
                <div class="button" @click=${(ev)=>this.updateBuild()}>Update</div>                
                `:html`
                <div class="button" @click=${(ev)=>this.createBuild()}>Create</div>                
                `}                
            `;
        }
        
        if(!this.build.name)
            return html``;   

        if(this.error)
            return html`<p style="color:red">${this.error}</p>`;   

        const cls = DATA_CLASSES.filter(c=>this.build?.class?.indexOf(c.name)!==-1)[0];

        return html`
            <h1><card-part id=${this.build.artwork} imageonly="true" header="true"></card-part> ${this.build.name}</h1>            
            ${!this.build.discorduser?html`
                <h3 style="color:red">This build is unverified. Please run the <code>/${this.build.updating?'updatebuild':'createbuild'}</code> command in the Discord to verify this build.<br><br>Copy the Build ID below:</h3>                
                <p style="color:red;font-weight:bold;">${this.build.uid}</p>
                <br>
            `:''}
            <div class="bubble big class-${(cls?.name||'scion').toLowerCase()}" style="font-weight:bold;margin-left:0;">
                ${cls?html`
                    <img loading="lazy" class="class-image" src='./images/classes/${cls.artwork}.webp' style='margin-left: 5px' alt=${cls.name} />          
                `:html`
                    <img loading="lazy" class="class-image" src='./images/icons/cardart_5_50.webp' style='margin-left: 5px' alt="Sunforge Run" />     
                `}   
                ${convertName(this.build.class)}  
            </div>
            <div class="bubble big">
                <b>Difficulty:</b> ${this.build.diff}
            </div>
            <div class="bubble big">
                <b>Version:</b> ${this.build.version}
            </div>
            ${this.build.uid?html`                             
                <p style="color:#CCC;font-size:14px;">
                    Posted: ${new Date(this.build.uid).toLocaleString()} ${this.build.discorduser?html`by <discorduser-component user=${this.build.discorduser}></discorduser-component>`:''}
                </p>                
            `:''}  
            ${Object.keys(this.parts).map((k,i)=>html`
                ${this.build.parts[i] ? html`
                    ${i > 0 ? html`<h2>${k}</h2>`:''}
                    ${parseHTML(this.build.parts[i],true)}
                `:''}
            `)}            
            ${this.build.discorduser?html`
            <br><br>
            <div class="button" @click=${(ev)=>window.location = `./build/update?id=${this.build.uid}`}>Update Build</div>
            <br>  
            <small>* Requires verification as ${this.build.discorduser}.</small>
            `:''}            
            <comments-component path=${window.location.pathname}></comments-component>
        `;
    }
}

customElements.define('build-page', BuildPage);